<template>
  <div v-if="showPros" :class="spacingClasses">
    <ProsCollection :recommendation-config="recommendationConfig" :panel-config="prosPanelConfig" />
  </div>
</template>

<script setup>
import { watch, computed, ref, toValue } from 'vue';
import { useRecommendationStore } from '@nuxt-common/pros-collection/app/stores/recommendation';
import ProsCollection from '@nuxt-common/pros-collection/app/components/ProsCollection.vue';
import { usePageData } from '~/composables/usePageData';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import useColor from '~/modules/color/composables/useColor';
import useUpc from '~/modules/upc/composables/useUpc';
import parseFacetsFromUrl from '~/utils/parseUrl';

const { prosInNewTab, shoppableRedesignOptimization } = useFeatureEligibility();
const store = useRecommendationStore();
const { taxonomy } = useCommonProductGetters();
const categoryId = toValue(taxonomy)?.defaultCategoryId;

const { productId, product, killswitches } = usePageData();
const { detail, traits, imagery } = product.product;
const spacingClasses = shoppableRedesignOptimization
  ? 'small-margin-bottom-m large-margin-bottom-xl'
  : 'small-margin-bottom-xl large-margin-bottom-xxl';

const prosPanelConfig = {
  openInNewTab: prosInNewTab,
  properties: killswitches,
  meta: {
    product: {
      id: productId,
      detail,
      traits,
      imagery,
    },
  },
  customStyleClass: 'custom-zone-bf-styling',
  customHeaderType: 'div',
  customRole: 'heading',
  customAriaLevel: '4',
};
const showPros = ref(false);
const selectedMemberId = useSelectedMemberId();
const visitedIds = [selectedMemberId.value];
const { selectedColor } = useColor();
const { selectedUpcId } = useUpc();

const selectedSize = computed(() => {
  const productSizeAttributes = product?.product?.relationships?.upcs?.[selectedUpcId.value]?.attributes;

  return productSizeAttributes?.reduce((acc, { name, value }) => `${acc};${name}:${value}`, '').slice(1);
});

const recommendationConfig = computed(() => ({
  zone: 'PDP_ZONE_B',
  productId,
  memberProductId: selectedMemberId.value,
  categoryId,
  selectedColor: selectedColor.value?.name,
  selectedColorFamily: selectedColor.value?.normalName,
  selectedSize: selectedSize.value,
  ...parseFacetsFromUrl(),
}));
// add experiment ids check;  && experimentIds.indexOf('1161-21') != -1

const productIds = computed(() => store.getRecommendationProductIds);

if (productIds.value?.PDP_ZONE_A !== undefined) {
  recommendationConfig.value.productIds = productIds.value.PDP_ZONE_A;
  showPros.value = true;
}

watch(
  () => selectedMemberId.value,
  (id) => {
    if (!visitedIds.includes(id)) {
      showPros.value = false;
      visitedIds.push(id);
    }
  },
);

watch(
  () => productIds.value,
  (recommendedProducts) => {
    if (productIds.value?.PDP_ZONE_A !== undefined) {
      recommendationConfig.value.productIds = recommendedProducts.PDP_ZONE_A;
    }

    showPros.value = true;
  },
  { deep: true },
);
</script>

<style lang="scss">
@import '~@core/csg/src/scss/typography/typography-mixins.scss';

.custom-zone-bf-styling {
  @include breakpoint(small only) {
    .pros-grid-header {
      @include header(h4);
    }
    .header-container > div:first-child {
      @include header(h4);
    }
  }

  div.pros-grid-header {
    @include header(h4);
  }

  .header-container > div:first-child {
    @include header(h4);
  }
}
</style>
